import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  model,
  NgZone,
  signal,
  untracked,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { TabsModule } from 'primeng/tabs';

/** One tab of `sh-tabs`. */
export interface ShTab {
  key: string;
  /** Translation key or plain text. */
  label: string;
  /** Shown after the label ("Services · 24"). */
  badge?: string | number;
  disabled?: boolean;
}

/**
 * The key a `?tab=` value stands for: a tab key, or (legacy links) the index
 * of a tab in the order of `tabs`. `undefined` when it is neither.
 */
export function resolveTabKey(
  value: string | null | undefined,
  tabs: readonly ShTab[]
): string | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const byKey = tabs.find((tab) => tab.key === value);
  if (byKey) return byKey.key;
  if (/^\d+$/.test(value)) return tabs[Number(value)]?.key;
  return undefined;
}

/**
 * The tab bar. It renders the tabs only; the panels are rendered by
 * `sh-tab-panels`, which can sit anywhere in the same template. That is what
 * lets a detail page put the bar in the header (`sh-detail-header`'s
 * `[detailTabs]` slot) and the panels in the body, and lets the peek panel
 * keep the bar fixed above scrolling panels.
 *
 *   <sh-tabs #tabs [tabs]="tabList" [(active)]="activeTab" />
 *   <sh-tab-panels [for]="tabs">
 *     <ng-template shTabPanel="details">Details</ng-template>
 *     <ng-template shTabPanel="history">History</ng-template>
 *   </sh-tab-panels>
 *
 * `active` is the key of the open tab (the first tab when it is not set).
 * With `urlParam="tab"` the open tab lives in `?tab=<key>`: it is read from
 * the URL (a legacy numeric index is mapped to that position in `tabs`), a
 * tab change is written with `replaceUrl` and the other query params are
 * kept; a value equal to the open tab is ignored. `urlState` is passed as the
 * navigation state of those writes (a page that keeps its back link there).
 * With a `urlParam` the URL is the source of truth: no param means the first
 * tab.
 *
 * The open tab is scrolled into view once after the first render, so a page
 * opened on a tab beyond the scrolled strip does not show "no active tab".
 */
@Component({
  selector: 'sh-tabs',
  // Default, not OnPush: the labels and badges are plain values of inputs, but
  // pages build `tabs` from state that changes outside them.
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [CommonModule, TranslateModule, TabsModule],
  template: `
    <p-tabs [value]="current()" (valueChange)="select($event)">
      <p-tablist>
        <p-tab
          *ngFor="let tab of tabs(); trackBy: trackByKey"
          [value]="tab.key"
          [disabled]="!!tab.disabled"
          >{{ tab.label | translate
          }}<ng-container *ngIf="hasBadge(tab)">
            · {{ tab.badge }}</ng-container
          ></p-tab
        >
      </p-tablist>
    </p-tabs>
  `,
})
export class ShTabsComponent implements AfterViewInit {
  readonly tabs = input<ShTab[]>([]);
  /** The key of the open tab. */
  readonly active = model<string>();
  /** Query parameter that holds the open tab (e.g. `'tab'`); none = no URL binding. */
  readonly urlParam = input<string>();
  /** Navigation state for URL writes. */
  readonly urlState = input<Record<string, unknown>>();

  private readonly _route = inject(ActivatedRoute, { optional: true });
  private readonly _router = inject(Router, { optional: true });
  private readonly _host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly _zone = inject(NgZone);

  // The raw `?tab=` value of the route, `null` when absent.
  private readonly _urlValue = signal<string | null>(null);

  /** The key of the open tab: `active` when it is one of the tabs, else the first tab. */
  readonly current = computed(() => {
    const active = this.active();
    const tabs = this.tabs();
    return active !== undefined && tabs.some((tab) => tab.key === active)
      ? active
      : tabs[0]?.key;
  });

  constructor() {
    // URL -> tabs: follow the route's query param.
    effect(
      (onCleanup) => {
        const param = this.urlParam();
        if (!param || !this._route) return;
        const subscription = this._route.queryParamMap.subscribe((params) =>
          this._urlValue.set(params.get(param))
        );
        onCleanup(() => subscription.unsubscribe());
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        if (!this.urlParam() || !this._route) return;
        const tabs = this.tabs();
        const key = resolveTabKey(this._urlValue(), tabs) ?? tabs[0]?.key;
        if (key !== undefined && key !== untracked(() => this.current())) {
          this.active.set(key);
          this._scrollActiveIntoView();
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngAfterViewInit(): void {
    this._scrollActiveIntoView();
  }

  protected select(value: string | number | undefined): void {
    if (value === undefined) return;
    const key = String(value);
    // PrimeNG can emit on init; anything that is not a change is ignored.
    if (key === this.current()) return;
    if (this.tabs().find((tab) => tab.key === key)?.disabled) return;
    this.active.set(key);
    this._writeUrl(key);
  }

  protected hasBadge(tab: ShTab): boolean {
    return tab.badge !== undefined && tab.badge !== null;
  }

  protected readonly trackByKey = (_: number, tab: ShTab) => tab.key;

  private _writeUrl(key: string): void {
    const param = this.urlParam();
    if (!param || !this._router || !this._route) return;
    if (this._urlValue() === key) return;
    void this._router.navigate([], {
      relativeTo: this._route,
      queryParams: { [param]: key },
      queryParamsHandling: 'merge',
      replaceUrl: true,
      state: this.urlState(),
    });
  }

  private _scrollActiveIntoView(): void {
    if (typeof requestAnimationFrame === 'undefined') return;
    this._zone.runOutsideAngular(() =>
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          const active =
            this._host.nativeElement.querySelector<HTMLElement>(
              '.p-tab-active'
            );
          active?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
        })
      )
    );
  }
}
